/**
 * Utility functions for adding image resize parameters to URLs
 */

export interface ImageResizeOptions {
  width?: number;
  height?: number;
  format?: 'webp';
}

/**
 * Adds resize parameters to an image URL
 * @param url - The original image URL
 * @param options - Resize options (width, height, format)
 * @param context - Optional context for logging (e.g., 'Series Card', 'Search Result')
 * @returns URL with resize parameters appended
 */
export function addImageResizeParams(
  url: string | null | undefined,
  options: ImageResizeOptions,
  context?: string
): string {
  if (!url) {
    //console.log(`🖼️ [Image Resize] Using placeholder for ${context || 'unknown'}`);
    return 'https://images.unsplash.com/photo-1518709268805-4e9042af2176';
  }

  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);

    if (options.width) {
      params.set('width', options.width.toString());
    }

    if (options.height) {
      params.set('height', options.height.toString());
    }

    if (options.format) {
      params.set('format', options.format);
    }

    urlObj.search = params.toString();
    const resizedUrl = urlObj.toString();

    //console.log(`🖼️ [Image Resize] ${context || 'Image'} | Size: ${options.width}x${options.height} | Format: ${options.format || 'default'} | URL: ${resizedUrl}`);

    return resizedUrl;
  } catch (error) {
    //console.warn(`🖼️ [Image Resize] Invalid URL for ${context || 'unknown'}:`, url, error);
    return url || 'https://images.unsplash.com/photo-1518709268805-4e9042af2176';
  }
}

/**
 * Common image size presets for different use cases
 */
export const ImageSizePresets = {
  /** Series card thumbnail (vertical) - 128x171 */
  serieCardVertical: { width: 256, height: 342, format: 'webp' as const },

  /** Series card thumbnail (horizontal) - 96x128 */
  serieCardHorizontal: { width: 192, height: 256, format: 'webp' as const },

  /** Highlight carousel banner - full width */
  highlightBanner: { width: 1920, height: 1080, format: 'webp' as const },

  /** Episode thumbnail */
  episodeThumbnail: { width: 640, height: 360, format: 'webp' as const },

  /** Search result thumbnail (portrait 3:4) */
  searchThumbnail: { width: 120, height: 160, format: 'webp' as const },

  /** Player series info thumbnail */
  playerSeriesInfo: { width: 480, height: 270, format: 'webp' as const },

  /** For You view series thumbnail */
  forYouSeries: { width: 512, height: 288, format: 'webp' as const },

  /** Series card landscape (16:9) for web desktop rails */
  serieCardLandscape: { width: 480, height: 270, format: 'webp' as const },
} as const;

export function ensureWebpFormat(url: string | null | undefined): string {
  if (!url) return '';
  if (!url.includes('warehouse.galaxydve.com')) return url;
  if (url.includes('format=webp')) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}format=webp`;
}

/**
 * Like {@link ensureWebpFormat} but also requests a resized image when a target
 * width is known. Used on web to avoid downloading full-resolution covers into
 * small card slots (PageSpeed "avoid enormous network payloads"). Existing
 * `format`/`width` query params are preserved and never overridden.
 */
export function ensureWebpFormatWithWidth(
  url: string | null | undefined,
  width?: number
): string {
  if (!url) return '';
  if (!url.includes('warehouse.galaxydve.com')) return url;

  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams(urlObj.search);
    if (!params.has('format')) {
      params.set('format', 'webp');
    }
    if (width && width > 0 && !params.has('width')) {
      params.set('width', Math.round(width).toString());
    }
    urlObj.search = params.toString();
    return urlObj.toString();
  } catch {
    return ensureWebpFormat(url);
  }
}
